import { memo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, useMapContext } from 'react-simple-maps';
import { Ship, Truck } from 'lucide-react';
import type { LugarDeComercia } from '../../types/comercia.types';
import {
  ciudadDe,
  coordenadasDe,
  longitudDelMedio,
  nombreDelPunto,
  rotuloDelPunto,
} from '../../utils/lugares';

/** El dibujo del mundo, servido por la app (no depende de internet). */
const TIERRA = '/mapas/tierra-110m.json';

/**
 * Los colores del mapa salen del tema (clases, no atributos: un `fill` con
 * `var()` no lo resuelven todos los navegadores). El naranja es el de Comercia.
 */
const NARANJA = '#C2410C';

/**
 * El «mapa ilustrativo» de Origen y destino (maqueta de Melanie): el tramo por
 * mar del puerto de salida al de llegada, punteado, y debajo los dos tramos: el
 * marítimo y el terrestre, con su mapa. Es para ubicarse, no para navegar: los
 * números de la ruta salen en Rutas.
 */
export const MapaDeRuta = memo(function MapaDeRuta({
  origen,
  puerto,
  destino,
}: {
  origen: LugarDeComercia | null;
  puerto: LugarDeComercia | null;
  destino: LugarDeComercia | null;
}) {
  const o = origen ? coordenadasDe(origen) : null;
  const p = puerto ? coordenadasDe(puerto) : null;
  const d = destino ? coordenadasDe(destino) : null;
  const llegada = p ?? d;

  // Se centra en el medio del arco más corto: de China a Chile, cruza el Pacífico.
  const medio = o && llegada ? longitudDelMedio(o, llegada) : -100;
  const latitudMedia = o && llegada ? (o[1] + llegada[1]) / 2 : 5;
  const separacion = o && llegada ? Math.abs(((o[0] - llegada[0] + 540) % 360) - 180) : 160;
  const ANCHO = 760;
  const ALTO = 330;
  const escala = Math.min(320, (ANCHO * 0.8) / (((separacion + 40) * Math.PI) / 180));
  const pacifico = medio > 150 || medio < -110;

  const descripcion =
    origen && destino
      ? `Mapa ilustrativo: de ${ciudadDe(origen)} ${puerto ? `por mar hasta ${ciudadDe(puerto)} y por tierra ` : ''}hasta ${ciudadDe(destino)}.`
      : 'Mapa ilustrativo: elige el origen y el destino.';

  return (
    <div className="space-y-3">
      <div
        className="relative overflow-hidden rounded-2xl bg-muted/50"
        role="img"
        aria-label={descripcion}
      >
        <ComposableMap
          width={ANCHO}
          height={ALTO}
          projection="geoMercator"
          projectionConfig={{ rotate: [-medio, 0, 0], center: [0, latitudMedia], scale: escala }}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          <Geographies geography={TIERRA}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  className="fill-border stroke-border"
                  strokeWidth={0.4}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
          {o && llegada && (
            <Arco
              desde={o}
              hasta={llegada}
              className="stroke-primary"
              ancho={2.4}
              punteado="7 6"
              curva={0.18}
              rotulo="marítimo"
              tamanoRotulo={14}
            />
          )}
          {o && origen && (
            <Punto
              coordenadas={o}
              claseColor="fill-orange-700"
              titulo={origen}
              detalle={`${nombreDelPunto(origen) === ciudadDe(origen) ? 'Puerto' : `Puerto de ${nombreDelPunto(origen)}`} · origen`}
              lado="derecha"
              arriba
            />
          )}
          {p && puerto && (
            <Punto
              coordenadas={p}
              claseColor="fill-primary"
              titulo={puerto}
              detalle="puerto de llegada"
              lado="izquierda"
              abajo
            />
          )}
          {d && destino && (!p || Math.hypot(d[0] - p[0], d[1] - p[1]) > 4) && (
            <Punto
              coordenadas={d}
              claseColor="fill-primary"
              titulo={destino}
              detalle="destino final"
              lado="derecha"
              arriba
            />
          )}
          {o && llegada && pacifico && (
            // Arriba y a la derecha del arco: en el medio chocaba con «marítimo».
            <text
              x={ANCHO * 0.64}
              y={ALTO * 0.36}
              className="fill-muted-foreground"
              fontSize={15}
              fontStyle="italic"
              textAnchor="middle"
            >
              Océano Pacífico
            </text>
          )}
        </ComposableMap>

        {!o && (
          <p className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
            Elige el origen y el destino para ver el recorrido.
          </p>
        )}
      </div>

      {/*
      Los tramos, debajo y no encima del mapa: el recuadro terrestre flotaba
      sobre el Pacífico y tapaba justo la llegada a Sudamérica (pedido de
      Joaquín, 28-oct). Cada tramo en su tarjeta, el terrestre con su mapa.
    */}
      {o && origen && p && d && puerto && destino && (
        <div className="grid gap-3 sm:grid-cols-2">
          <TramoMaritimo origen={origen} puerto={puerto} />
          <TramoTerrestre puerto={puerto} destino={destino} p={p} d={d} />
        </div>
      )}
    </div>
  );
});

/** El tramo por mar, de puerto a puerto. En el mapa grande ya se ve el arco. */
function TramoMaritimo({ origen, puerto }: { origen: LugarDeComercia; puerto: LugarDeComercia }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <EncabezadoDeTramo
        icono={<Ship className="h-4 w-4" aria-hidden="true" />}
        tono="bg-primary/10 text-primary"
        titulo="TRAMO MARÍTIMO"
        recorrido={`${ciudadDe(origen)} → ${ciudadDe(puerto)}`}
      />
      {/* De puerto a puerto, en una línea: el arco sobre el océano ya está arriba. */}
      <div className="flex h-24 items-center gap-3 bg-muted/50 px-5">
        <ExtremoDeTramo lugar={origen} />
        <div className="relative flex-1 border-t-2 border-dashed border-primary">
          <span className="absolute left-1/2 top-0 grid h-7 w-7 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border bg-card text-primary shadow-sm">
            <Ship className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
        <ExtremoDeTramo lugar={puerto} alFinal />
      </div>
    </div>
  );
}

function EncabezadoDeTramo({
  icono,
  tono,
  titulo,
  recorrido,
}: {
  icono: React.ReactNode;
  tono: string;
  titulo: string;
  recorrido: string;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${tono}`}>
        {icono}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold tracking-[0.12em] text-muted-foreground">{titulo}</p>
        <p className="truncate font-semibold">{recorrido}</p>
      </div>
    </div>
  );
}

function ExtremoDeTramo({ lugar, alFinal }: { lugar: LugarDeComercia; alFinal?: boolean }) {
  return (
    <div className={`min-w-0 shrink-0 ${alFinal ? 'text-right' : ''}`}>
      <p className="text-sm font-semibold">
        <span className="text-[10px] font-extrabold">{lugar.pais ?? ''}</span> {ciudadDe(lugar)}
      </p>
      {/* Si el puerto se llama como la ciudad, «Shanghái / Shanghái» no dice nada: va su papel. */}
      <p className="max-w-[140px] truncate text-[11px] text-muted-foreground">
        {nombreDelPunto(lugar) === ciudadDe(lugar)
          ? alFinal
            ? 'puerto de llegada'
            : 'puerto de salida'
          : rotuloDelPunto(lugar)}
      </p>
    </div>
  );
}

function Punto({
  coordenadas,
  claseColor,
  titulo,
  detalle,
  lado,
  abajo,
  arriba,
}: {
  coordenadas: [number, number];
  claseColor: string;
  titulo: LugarDeComercia;
  detalle: string;
  lado: 'izquierda' | 'derecha';
  abajo?: boolean;
  /** Los dos renglones arriba del punto: deja libre la línea que sale de él. */
  arriba?: boolean;
}) {
  const x = lado === 'derecha' ? 10 : -10;
  const ancla = lado === 'derecha' ? 'start' : 'end';
  const y = abajo ? 20 : arriba ? -28 : -12;
  return (
    <Marker coordinates={coordenadas}>
      <circle r={6.5} className={claseColor} stroke="white" strokeWidth={2.2} />
      <text
        x={x}
        y={y}
        textAnchor={ancla}
        className="fill-foreground"
        fontSize={15}
        fontWeight={700}
      >
        <tspan fontSize={11} fontWeight={800}>
          {titulo.pais ?? ''}{' '}
        </tspan>
        {ciudadDe(titulo)}
      </text>
      <text x={x} y={y + 16} textAnchor={ancla} className="fill-muted-foreground" fontSize={12}>
        {detalle}
      </text>
    </Marker>
  );
}

/**
 * El tramo del puerto a la ciudad, en grande: en el mapa del mundo son
 * milímetros. Con el contorno de los países, para que se vea que cruza la
 * frontera.
 */
function TramoTerrestre({
  puerto,
  destino,
  p,
  d,
}: {
  puerto: LugarDeComercia;
  destino: LugarDeComercia;
  p: [number, number];
  d: [number, number];
}) {
  const centro: [number, number] = [(p[0] + d[0]) / 2, (p[1] + d[1]) / 2];
  const distancia = Math.max(2, Math.hypot(p[0] - d[0], p[1] - d[1]));
  // Que el tramo ocupe más o menos la mitad del ancho, con lugar para los nombres.
  const escala = Math.min(4200, 150 / ((distancia * Math.PI) / 180));
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <EncabezadoDeTramo
        icono={<Truck className="h-4 w-4" aria-hidden="true" />}
        tono="bg-orange-700/10 text-orange-700"
        titulo="TRAMO TERRESTRE"
        recorrido={`${ciudadDe(puerto)} → ${ciudadDe(destino)}`}
      />
      {/* Los mismos colores del mapa grande: el mar claro, la tierra y sus fronteras. */}
      <ComposableMap
        width={300}
        height={96}
        projection="geoMercator"
        projectionConfig={{ center: centro, scale: escala }}
        className="bg-muted/50"
        style={{ width: '100%', height: 96, display: 'block' }}
      >
        <Geographies geography={TIERRA}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                className="fill-border stroke-card"
                strokeWidth={0.8}
                style={{
                  default: { outline: 'none' },
                  hover: { outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>
        <Arco
          desde={p}
          hasta={d}
          color={NARANJA}
          ancho={2.6}
          curva={0.12}
          rotulo="camión"
          claseRotulo="fill-orange-700"
        />
        <Marker coordinates={p}>
          <circle r={4.5} className="fill-primary" stroke="white" strokeWidth={1.6} />
          <text
            x={0}
            y={16}
            textAnchor="middle"
            className="fill-foreground"
            fontSize={10}
            fontWeight={700}
          >
            <tspan fontSize={8}>{puerto.pais ?? ''} </tspan>
            {ciudadDe(puerto)}
          </text>
        </Marker>
        <Marker coordinates={d}>
          <circle r={4.5} className="fill-primary" stroke="white" strokeWidth={1.6} />
          <text
            x={0}
            y={-9}
            textAnchor="middle"
            className="fill-foreground"
            fontSize={10}
            fontWeight={700}
          >
            <tspan fontSize={8}>{destino.pais ?? ''} </tspan>
            {ciudadDe(destino)}
          </text>
        </Marker>
      </ComposableMap>
    </div>
  );
}

/**
 * Un tramo dibujado como un arco suave entre dos puntos ya proyectados.
 *
 * No es la línea geodésica: de Shanghái a Arica el círculo máximo sube hasta
 * el Pacífico norte y se sale del recuadro. Para ubicarse alcanza con un arco
 * que una los dos puertos, como en la maqueta.
 */
function Arco({
  desde,
  hasta,
  className,
  color,
  ancho,
  punteado,
  curva,
  rotulo,
  claseRotulo = 'fill-primary',
  tamanoRotulo = 12,
}: {
  desde: [number, number];
  hasta: [number, number];
  className?: string;
  color?: string;
  ancho: number;
  punteado?: string;
  /** Cuánto se separa del medio, como fracción del largo del tramo. */
  curva: number;
  rotulo?: string;
  claseRotulo?: string;
  tamanoRotulo?: number;
}) {
  const { projection } = useMapContext();
  const a = projection(desde);
  const b = projection(hasta);
  if (!a || !b) return null;
  const [x1, y1] = a;
  const [x2, y2] = b;
  const largo = Math.hypot(x2 - x1, y2 - y1);
  // El control va perpendicular al tramo, hacia arriba.
  const nx = -(y2 - y1) / (largo || 1);
  const ny = (x2 - x1) / (largo || 1);
  const signo = ny > 0 ? -1 : 1;
  const cx = (x1 + x2) / 2 + nx * largo * curva * signo;
  const cy = (y1 + y2) / 2 + ny * largo * curva * signo;
  // El punto del medio de la curva, para el rótulo.
  const mx = 0.25 * x1 + 0.5 * cx + 0.25 * x2;
  const my = 0.25 * y1 + 0.5 * cy + 0.25 * y2;
  return (
    <g>
      <path
        d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`}
        fill="none"
        className={className}
        stroke={color}
        strokeWidth={ancho}
        strokeDasharray={punteado}
        strokeLinecap="round"
      />
      {rotulo && (
        <text
          x={mx}
          y={my + 4 + tamanoRotulo}
          textAnchor="middle"
          className={claseRotulo}
          fontSize={tamanoRotulo}
          fontWeight={600}
        >
          {rotulo}
        </text>
      )}
    </g>
  );
}
